import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { EidosConfigService } from '@app/core/config/eidos-config.service';
import { IEidosModuleConfiguration } from '@app/core/config/environment.interface';
import { CoreBaseApiCommandParams } from '@app/core/models/core-base-api.model';
import { CoreModuleName } from '@app/core/models/core-constant.model';
import { DynamicApiResponse } from '@app/core/models/dynamic-api-response.model';
import { CoreFormatService } from '@app/core/services/core-format.service';
import { EidosBaseApiService } from '@app/core/services/eidos-base-api.service';
import { EidosLogService } from '@app/core/services/eidos-log.service';
import { EidosSecurityService } from '@app/core/services/eidos-security.service';
import { IReservationApiSupplier, ReservationSupplier } from '@app/res-setup/models/resadm-service.models';
import { IReservationAirport, IReservationServiceType } from '@app/reservation/models/res-cached-data.models';
import { IReservationApiGetPackageParams, IReservationApiItemAvailability, ResItemAvailability, ResPackage } from '@app/reservation/models/res-package.model';
import { LoadOptions } from 'devextreme/data';
import { ResolvedData } from 'devextreme/data/custom_store';
import { Observable, catchError, filter, lastValueFrom, map } from 'rxjs';
import { IRescomApiInvokeAIParams, RescomApiInvokeAI } from '../models/rescom-ai.model';
import { IResComApiGetServicesSearch, IReservationApiGetServices, IReservationApiService, IReservationApiServiceType, IReservationGeotreeCityPicklist, ISupplierDetailsAddress, ISupplierDetailsBankAccount, ISupplierDetailsContact, IRescomApiItinerary, RescomItinerary, ReservationServiceData, ReservationServiceOption, ReservationServiceType, ReservationSetupService, ReservationSetupServiceOption, SupplierAddressType, SupplierContactType, SupplierDetails, SupplierDetailsAddress, SupplierDetailsBankServiceAccount, SupplierDetailsContact, RescomItineraryDetails } from '../models/rescom-service.model';
import { IResAPIGetGeoTreeCountryAndContinent, IResApiAddAmenities, IResApiGetAmenities, IResApiGetPackageUpgradeInventory, IResApiGetPropertiesLight, IResApiGetRules, IResApiGetUniqueSuppliers, IResApiManageRule, IResApiManageSupplierUnique, IResApiManageSupplierUniqueAddresses, IResApiManageSupplierUniqueContacts, IResApiPackageAllocations, IResComApiGetPropertiesSearch, IReservationApiGetSuppliers, IReservationModuleAPIConfiguration, ResAdmAmenity, ResAdmAmenityTree, ResAdmRules, ResBkgProductType, ResCheckInDay, ResCompanyLight, ResContinent, ResGeoTreeCountryAndContinent, ResLastWorkedBooking, ResPackageAllocation, ResPassportCountry, ResPkgBookingTravelDate, ResPriceSetupCurrencis, ResProperty, ResPropertyLight, ResPropertyLightSuiteCategory, ResReceiptType, ResRuleType, ResVillaAirConditioning, ResVillaDetails, ResVillaPriceRange, RescomUniqueSuppliers, RescomUpgradeInventory, ReservationSuiteAllocation, ReservationSuiteAllocationParams } from '../models/rescom.model';
import { IResApiGetDocumentationList, IResApiGetTopic, IResApiGetTopicSubjectList, ResAdmDocumentationList, ResAdmTopic, ResAdmTopicGuests, ResAdmTopicSubjectLookup } from '../models/rescom-topic.model';

@Injectable({
  providedIn: 'root'
})
export class RescomApiService extends EidosBaseApiService {

  constructor(
    public configService: EidosConfigService,
    public eidosSecurityService: EidosSecurityService,
    public http: HttpClient,
    public eidosLogService: EidosLogService,
    public dialog: MatDialog,
    public domSanitizer: DomSanitizer,
    public coreFormatService: CoreFormatService,
  ) {
    super(coreFormatService, configService, eidosLogService, dialog, eidosSecurityService, http);

    this.configService.currentModulesConfig
      .pipe(
        filter(
          (modules: Array<IEidosModuleConfiguration>) => !!modules.find((module) => module.moduleName === CoreModuleName.Reservation)
        )
      )
      .subscribe((modules) => {
        const module = modules.find((module) => module.moduleName === CoreModuleName.Reservation);

        if (module) {
          const reservationModuleAPI = module.API as IReservationModuleAPIConfiguration;
          this.urlAPI = reservationModuleAPI.url;
          this.staticUrlAPI = reservationModuleAPI.staticUrl;
          this.pluginUrlAPI = reservationModuleAPI.pluginUrl;
          this.apiVersion = reservationModuleAPI.version;
          if (reservationModuleAPI.headers) {
            this.headers = {
              ...reservationModuleAPI.headers,
              ...this.headers
            };
          }
        }
      });
  }

  private titleCaseWord(word: string) {
    if (!word) return word;
    return word[0].toUpperCase() + word.substring(1);
  }

  private localSort(items: [], loadOptions: any): [] {
    return items.sort((i1, i2) => {
      let field = loadOptions.sort[0].selector;
      let desc = loadOptions.sort[0].desc;
      let item1 = i1[field];
      if (!item1) {
        item1 = i1[this.titleCaseWord(field)];
      }
      let item2 = i2[field];
      if (!item2) {
        item2 = i2[this.titleCaseWord(field)];
      }
      if (item1 > item2) {
        return desc ? -1 : 1;
      }
      if (item1 < item2) {
        return desc ? 1 : -1;
      }
      return 0;
    });
  }

  private fillPaginatedParams(params?: any, loadOptions?: LoadOptions) {
    if (!params) params = {};

    if (!loadOptions) return params;

    const skip = loadOptions.skip ?? 0;
    const take = loadOptions.take ?? 20;

    params.PageNum = Math.floor(skip / take) + 1;
    params.PageSize = take;

    if (!!loadOptions.filter) {
      if (Array.isArray(loadOptions.filter[0])) {
        loadOptions.filter.forEach((f: any) => {
          if (Array.isArray(f)) {
            let field: string = f[0];
            let cmp: string = f[2];
            params[field] = cmp;
          }
        });
      } else {
        let field: string = loadOptions.filter[0];
        let cmp: string = loadOptions.filter[2];
        params[field] = cmp;
      }
    }

    if (!!loadOptions.sort) {
      const sort = loadOptions.sort as { selector: string; desc: boolean }[];

      params.OrderField = sort[0]?.selector;
      params.OrderDirect = sort[0]?.desc ? 1 : 0;
    }

    return params;
  }
  supplierCache:{ key: string,data:  Promise<ReservationSupplier[]> }[] = []
  getSuppliersMemoize(search?: string, id?: number) {
    const key = `${id}-${search}`
    const data = this.supplierCache.find(k=>k.key===key)?.data
    if(data) return data
    const newData = lastValueFrom(
                      this.getSuppliers({
                        SupplierID: (id === 0 ? undefined : id),
                        SupplierName: (search==='' ? undefined : search )
                      })
                    )
    this.supplierCache.push({
          key: key,
          data: newData
        })
    return newData
  }

  retriveWorkedBookingList(): Observable<ResLastWorkedBooking[]> {
    return this.callDynamicAPI('user/lastWorkedBooking', {}).pipe(
      map<DynamicApiResponse, ResLastWorkedBooking[]>((response) => response.body.map((s: any) => new ResLastWorkedBooking(s)))
    );
  }

  getSuppliers(params?: IReservationApiGetSuppliers): Observable<ReservationSupplier[]> {
    return this.callDynamicAPI('serviceSupplier/select', params).pipe(
      map<DynamicApiResponse, ReservationSupplier[]>((response) => response.body.map((s: IReservationApiSupplier) => new ReservationSupplier(s)))
    );
  }

  getPackageHeader(params?: IReservationApiGetPackageParams): Observable<ResPackage[]> {
    return this.callDynamicAPI('packageHeader/select', params).pipe(
      map<DynamicApiResponse, ResPackage[]>((response) => response.body.map((s: any) => new ResPackage(s)))
    );
  }

  getPackageHeaderPaginated(params?: IReservationApiGetPackageParams, loadOptions?: any): Promise<ResolvedData<ResPackage>> {
    params = this.fillPaginatedParams(params, loadOptions);

    return lastValueFrom(
      this.callDynamicAPI('packageHeader/select', params).pipe(
        map<DynamicApiResponse, ResolvedData<ResPackage>>((response: DynamicApiResponse) => {
          return {
            data: response.body.map((o: any) => new ResPackage(o)),
            totalCount: response.recordsCount,
          };
        })
      )
    ).catch(() => {
      throw 'Data loading errors';
    });
  }

  getItemAvailability(params:IReservationApiItemAvailability) {
    return this.callDynamicAPI('item/availability', params, 'POST').pipe(
      map<DynamicApiResponse, ResItemAvailability[]>(
        (response: DynamicApiResponse) => response.body.map((o: any) => new ResItemAvailability(o)))
    )
  }

  getVilla(villaID: number): Observable<ResVillaDetails> {
    return this.getDynamicAPI('villa/getVilla', { VillaID: villaID }).pipe(
      map<DynamicApiResponse, ResVillaDetails>((response) => {
        return new ResVillaDetails(response.body)
      })
    );
  }

  public getService(params?: IResComApiGetServicesSearch): Observable<ReservationSetupService> {
    return this.callDynamicAPI('service/select', params).pipe(
      map<DynamicApiResponse, ReservationSetupService>((response) => {
        return new ReservationSetupService(response.body[0]);
      })
    );
  }

  public getServicesPaginated(params?: IResComApiGetServicesSearch, loadOptions?: any): Promise<ResolvedData<ReservationSetupService>> {
    params = this.fillPaginatedParams(params, loadOptions);

    return lastValueFrom(
      this.callDynamicAPI('service/select', params).pipe(
        map<DynamicApiResponse, ResolvedData<ReservationSetupService>>((response: DynamicApiResponse) => {
          return {
            data: response.body.map((o: any) => new ReservationSetupService(o)),
            totalCount: response.recordsCount,
          };
        })
      )
    ).catch(() => {
      throw 'Data loading errors';
    });
  }

  public getLinkedServiceOptions(serviceID: number): Observable<ReservationServiceOption[]> {
    return this.callDynamicAPI('service/getLinkedServices', { ServiceID: serviceID }).pipe(
			map<DynamicApiResponse, ReservationServiceOption[]>(response => {
				return response.body.map((o: any) => ({
					...o,
					serviceID: o.ServiceIDLnk,
          optionID: o.OptionID,
          cityID: o.CityID,
				}))
			})
		)
  }

  public manageService(params?: ReservationSetupService): Observable<any> {
    return this.postDynamicAPI('service/manage', params);
  }

  getItinerary(prms:IRescomApiItinerary): Observable<RescomItinerary[]> {
    return this.getDynamicAPI('product/itinerary', prms).pipe(
      map<DynamicApiResponse, RescomItinerary[]>(
        (response) => {
          const items:RescomItinerary[] = []
          const itineary = response.body.DT0 ?? []
          const details = response.body.DT1 ?? []
          itineary.forEach((item:any)=>{
            const itemItinerary = new RescomItinerary(item)
            itemItinerary.details = details.filter((det:any)=>
                                            det.ProductID===item.ProductID && det.Day===item.Day
                                           )
                                           .map((det:any)=> new RescomItineraryDetails(det))
            items.push(itemItinerary)
          })
          return items
        }
      ),
      catchError<any, RescomItinerary[]>((_: any) => [])
    );
  }
  public getServiceTypes(filters?: IReservationApiServiceType): Observable<ReservationServiceType[]> {
    return this.callDynamicAPI('serviceType/select', filters).pipe(
      map<DynamicApiResponse, Array<any>>((response) => {
        return response.body.map((c: IReservationServiceType) => new ReservationServiceType(c));
      })
    );
  }

  public getPicklistCitiesByID(id: number, departmentId = 1): Observable<IReservationGeotreeCityPicklist[]> {
    return this.callDynamicAPI('geotree/picklist', { GeoTreeID: id, departmentId: departmentId }).pipe(
      map<DynamicApiResponse, IReservationGeotreeCityPicklist[]>((response) => {
        return response.body
      })
    )
  }

  public getPicklistCities(city: string, departmentId = 1): Observable<IReservationGeotreeCityPicklist[]> {
    return this.callDynamicAPI('geotree/picklist', { GeoTreeName: city, DepartmentID: departmentId }).pipe(
      map<DynamicApiResponse, IReservationGeotreeCityPicklist[]>((response) => {
        return response.body
      })
    )
  }

  public getAirports(params: { AirportID?: number, AirName?: string }): Observable<Array<IReservationAirport>> {
    return this.callDynamicAPI('airport/select', params).pipe(
      map<DynamicApiResponse, Array<IReservationAirport>>((response) => {
        return response.body;
      })
    );
  }

  public getServices(params?: IReservationApiGetServices): Observable<ReservationServiceData[]> {
    return this.callDynamicAPI('service/select', params).pipe(
      map<DynamicApiResponse, Array<ReservationServiceData>>((response) => {
        return response.body.map((s: IReservationApiService) => new ReservationServiceData(s));
      })
    );
  }

  public getSetupServicesOptions(params: { ServiceIDOption?: number, ServiceID?: number, OptionID?: number, OptionName?: string, IsIncluded: 'Y' | 'N', IsActive?: 'Y', PriceSetup?: string, PageSize?: number }): Observable<ReservationSetupServiceOption[]> {
    return this.callDynamicAPI('option/select', params).pipe(
      map<DynamicApiResponse, Array<ReservationSetupServiceOption>>((response) => {
        return response.body.map((o: any) => new ReservationSetupServiceOption(o));
      })
    );
  }

  public getServicesOptionsPaginated(params?: any, loadOptions?: any): Promise<ResolvedData<ReservationSetupServiceOption>> {
    params = this.fillPaginatedParams(params, loadOptions);

    return lastValueFrom(
      this.callDynamicAPI('option/select', params).pipe(
        map<DynamicApiResponse, ResolvedData<ReservationSetupServiceOption>>((response: DynamicApiResponse) => {
          return {
            data: this.localSort(response.body, loadOptions).map((o: any) => new ReservationSetupServiceOption(o)),
            totalCount: response.recordsCount,
          };
        })
      )
    ).catch(() => {
      throw 'Data loading error';
    });
  }

  public manageSupplier(params?: ReservationServiceData): Observable<any> {
    return this.postDynamicAPI('serviceSupplier/manage', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body
      })
    )
  }

  public getSupplierDetails(supplierID: number): Observable<SupplierDetails> {
    return this.callDynamicAPI('supplier/getSupplierDetails', {SupplierID: supplierID}).pipe(
      map<DynamicApiResponse, SupplierDetails>((response) => {
        return new SupplierDetails(response.body);
      })
    );
  }

  public getAddressTypes(): Observable<SupplierAddressType[]> {
    return this.callDynamicAPI('supplier/getAddressTypes').pipe(
      map<DynamicApiResponse, SupplierAddressType[]>((response) => {
        return response.body.map((s: any) => new SupplierAddressType(s));
      })
    );
  }

  public getContactTypes(): Observable<SupplierContactType[]> {
    return this.callDynamicAPI('supplier/getContactTypes').pipe(
      map<DynamicApiResponse, SupplierContactType[]>((response) => {
        return response.body.map((s: any) => new SupplierContactType(s));
      })
    );
  }

  public getSupplierBankDetail(): Observable<SupplierDetailsBankServiceAccount[]> {
    return this.callDynamicAPI('supplier/getSupplierBankDetail').pipe(
      map<DynamicApiResponse, SupplierDetailsBankServiceAccount[]>((response) => {
        return response.body.map((s: any) => new SupplierDetailsBankServiceAccount(s));
      })
    );
  }

  public manageSupplierContacts(supplierID: number, supplierContacts?: ISupplierDetailsContact[]): Observable<any> {
    return this.postDynamicAPI('supplier/manageSupplierContact', {SupplierID: supplierID, SuppCont: supplierContacts}).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body
      })
    )
  }

  public manageSupplierAddresses(supplierID: number, supplierAddresses?: ISupplierDetailsAddress[]): Observable<any> {
    return this.postDynamicAPI('supplier/manageSupplierAddress', {SupplierID: supplierID, SupplierAddr: supplierAddresses}).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body
      })
    )
  }

  public manageSupplierBankDetail(supplierID: number, supplierBankDetail?: ISupplierDetailsBankAccount[]): Observable<any> {
    return this.postDynamicAPI('supplier/manageSupplierBankDetails', {SupplierID: supplierID, SuppBankDt: supplierBankDetail}).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body
      })
    )
  }

  public getCashReceiptTypes(companyID?: number, currencyCod?: string): Observable<ResReceiptType[]> {
    return this.callDynamicAPI('receiptType/select', { CompanyID: companyID, CurrencyCod: currencyCod}).pipe(
      map<DynamicApiResponse, ResReceiptType[]>((response) => {
        return response.body.map((s: any) => new ResReceiptType(s));
      })
    );
  }
  public getPackageBkgOnTravelDate(packageID: number, travelDateID: number): Observable<ResPkgBookingTravelDate[]> {
    return this.callDynamicAPI('package/BkgOnTravelDate', { PackageID: packageID, PackageTravelDateID: travelDateID}).pipe(
      map<DynamicApiResponse, ResPkgBookingTravelDate[]>((response) => {
        return response.body.map((s: any) => new ResPkgBookingTravelDate(s));
      })
    );
  }
  updatePackageBkgOnTravelDate(packageID: number, travelDateID: number): Observable<any> {
    return this.callDynamicAPI('package/updAllocBkg', { PackageID: packageID, PackageTravelDateID: travelDateID}).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    );
  }

  getPackageAllocationsPaginated(params?: IResApiPackageAllocations, loadOptions?: any): Promise<ResolvedData<ResPackageAllocation>> {
    params = this.fillPaginatedParams(params, loadOptions);

    return lastValueFrom(
      this.callDynamicAPI('package/getPackageAllocations', params).pipe(
        map<DynamicApiResponse, ResolvedData<ResPackageAllocation>>((response: DynamicApiResponse) => {
          return {
            data: response.body.map((o: any) => new ResPackageAllocation(o)),
            totalCount: response.recordsCount,
          };
        })
      )
    ).catch(() => {
      throw 'Data loading errors';
    });
  }

  getCheckInDay(): Observable<ResCheckInDay[]> {
    return this.callDynamicAPI('property/getCheckInDay').pipe(
      map<DynamicApiResponse, ResCheckInDay[]>((response) => {
        return response.body.map((s: any) => new ResCheckInDay(s));
      })
    );
  }

  getVillaPriceRange(): Observable<ResVillaPriceRange[]> {
    return this.callDynamicAPI('villa/getVillaPriceRange').pipe(
      map<DynamicApiResponse, ResVillaPriceRange[]>((response) => {
        return response.body.map((s: any) => new ResVillaPriceRange(s));
      })
    );
  }

  getVillaAirConditioning(): Observable<ResVillaAirConditioning[]> {
    return this.callDynamicAPI('villa/getVillaAirCond').pipe(
      map<DynamicApiResponse, ResVillaAirConditioning[]>((response) => {
        return response.body.map((s: any) => new ResVillaAirConditioning(s));
      })
    );
  }

  getSuiteVoyageAllocation(voyageID:number): Observable<ReservationSuiteAllocation[]> {
    return this.getSuiteAllocation( {
      voyageID:voyageID,
    });
  }
  getSuitePackageAllocation(
    packageID:number, packageTravelDateID: number
  ): Observable<ReservationSuiteAllocation[]> {
    return this.getSuiteAllocation( {
      packageID: packageID,
      packageTravelDateID: packageTravelDateID,
    });
  }

  getSuiteAllocation(filter: {
    packageID?:number,
    packageTravelDateID?: number,
    voyageID?: number
  }): Observable<ReservationSuiteAllocation[]> {
    return this.getDynamicAPI('suite/allocation', filter).pipe(
      map<DynamicApiResponse, ReservationSuiteAllocation[]>((response) => {
        const voyage = response.body.DT0 && response.body.DT0.length > 0 ?  response.body.DT0[0] : {}
        const suites =  response.body.DT1 && response.body.DT1.length > 0 ? response.body.DT1 : []
        return suites.map((s: any)=>new ReservationSuiteAllocation( Object.assign(s, voyage)) )
      })
    );
  }
  setSuiteAllocation(parameters: ReservationSuiteAllocationParams): Observable<boolean> {
    return this.postDynamicAPI('suite/setallocation', parameters).pipe(
      map<DynamicApiResponse, boolean>((_response) => {
        return true
      })
    );
  }

  getAllCompanies(): Observable<ResCompanyLight[]> {
    return this.callDynamicAPI('shorex/getAllCompanies').pipe(
      map<DynamicApiResponse, ResCompanyLight[]>((response) => {
        return response.body.map((s: any) => new ResCompanyLight(s));
      })
    );
  }

  getGeoCountryAndContinent(params?: IResAPIGetGeoTreeCountryAndContinent): Observable<ResGeoTreeCountryAndContinent[]> {
    return this.callDynamicAPI('geotree/getCountryAndContinent', params).pipe(
      map<DynamicApiResponse, ResGeoTreeCountryAndContinent[]>((response) => {
        return response.body.map((data: any) => new ResGeoTreeCountryAndContinent(data));
      })
    )
  }

  getGeoCountryAndContinentPaginated(params?: IResAPIGetGeoTreeCountryAndContinent, loadOptions?: any): Promise<ResolvedData<ResGeoTreeCountryAndContinent>> {
    params = this.fillPaginatedParams(params, loadOptions);

    return lastValueFrom(
      this.callDynamicAPI('geotree/getCountryAndContinent', params).pipe(
        map<DynamicApiResponse, ResolvedData<ResGeoTreeCountryAndContinent>>((response: DynamicApiResponse) => {
          return {
            data: response.body.map((o: any) => new ResGeoTreeCountryAndContinent(o)),
            totalCount: response.recordsCount,
          };
        })
      )
    ).catch(() => {
      throw 'Data loading errors';
    });
  }

  getTopicProductTypes(companiesID?: string): Observable<Array<ResBkgProductType>> {
    return this.callDynamicAPI('topic/getProductTypes', {CompaniesID: companiesID}).pipe(
      map<DynamicApiResponse, Array<ResBkgProductType>>((response) => {
        return response.body.map((data: any) => new ResBkgProductType(data));
      })
    )
  }

  getPassportCountry(country?: string, countryCode?: string): Observable<ResPassportCountry[]> {
    return this.callDynamicAPI('country/select', {Country: country, CountryCod: countryCode}).pipe(
      map<DynamicApiResponse, ResPassportCountry[]>((response) => {
        return response.body.map((data: any) => new ResPassportCountry(data));
      })
    )
  }

  getTopicPassportCountry(params?: {Country?: string, CountryCod?: string}): Observable<ResPassportCountry[]> {
    return this.callDynamicAPI('topic/getTopicCountry', params).pipe(
      map<DynamicApiResponse, ResPassportCountry[]>((response) => {
        return response.body.map((data: any) => new ResPassportCountry(data));
      })
    )
  }

  getContinent(): Observable<ResContinent[]> {
    return this.getDynamicAPI('pkg/getContinent').pipe(
      map<DynamicApiResponse, ResContinent[]>((response) => {
        return response.body.map((data: any) => new ResContinent(data))
      }),
    )
  }

  getProperties(params?: IResComApiGetPropertiesSearch): Observable<ResProperty[]> {
    return this.getDynamicAPI('property/get', params).pipe(
      map<DynamicApiResponse, ResProperty[]>((response) => {
        return response.body.map((data: any) => new ResProperty(data))
      })
    )
  }

  getPropertiesPaginated(params?: IResComApiGetPropertiesSearch, loadOptions?: any): Promise<ResolvedData<ResProperty>> {
    params = this.fillPaginatedParams(params, loadOptions);

    return lastValueFrom(
      this.callDynamicAPI('property/get', params).pipe(
        map<DynamicApiResponse, ResolvedData<ResProperty>>((response: DynamicApiResponse) => {
          return {
            data: response.body.map((o: any) => new ResProperty(o)),
            totalCount: response.recordsCount,
          };
        })
      )
    ).catch(() => {
      throw 'Data loading errors';
    });
  }

  getAmenitiesTree(params?: IResApiAddAmenities): Observable<ResAdmAmenityTree[]> {
    return this.callDynamicAPI('villas/getAmenitiesTree', params).pipe(
      map<DynamicApiResponse, ResAdmAmenityTree[]>((response) => {
        const categories = response.body?.DT1 ?? []
        return response.body?.DT0.map((o: any) => new ResAdmAmenityTree(o, categories)) ?? []
      })
    )
  }

  getAmenityByTier(params: IResApiGetAmenities): Observable<ResAdmAmenity[]> {
    return this.callDynamicAPI('villas/getAmenities', params).pipe(
      map<DynamicApiResponse, ResAdmAmenity[]>((response) => {
        return response.body.map((o: any) => new ResAdmAmenity(o))
      })
    )
  }

  getVillaCategories(): Observable<any[]> {
    return this.callDynamicAPI('villa/getLookup', {Scope: 'StyleCategory'}).pipe(
      map<DynamicApiResponse, any[]>((response) => {
        return response.body
      })
    )
  }

  getVillaRegions(): Observable<any[]> {
    return this.callDynamicAPI('villa/getLookup', {Scope: 'Region'}).pipe(
      map<DynamicApiResponse, any[]>((response) => {
        return response.body
      })
    )
  }

  getAllCurrencies(): Observable<{currencyCod: string}[]> {
    return this.callDynamicAPI('apc/getAllCurrencies').pipe(
      map<DynamicApiResponse, {currencyCod: string}[]>((response) => {
        return response.body.map((o: any) => ({currencyCod: o.CurrencyCod}))
      })
    )
  }

  getServiceFacilities(): Observable<{Scope: string, Cod: string, Desc: string, Position: number}[]> {
    return this.callDynamicAPI('service/getFacilities').pipe(
      map<DynamicApiResponse, {Scope: string, Cod: string, Desc: string, Position: number}[]>((response) => {
        return response.body
      })
    );
  }

  invokeAI(params: IRescomApiInvokeAIParams): Observable<RescomApiInvokeAI> {

    const commandParams = new CoreBaseApiCommandParams({
      url: 'oem/ai/copywriter',
      data: params,
      type: 'POST',
      customBaseUrl: this.pluginUrlAPI
    });

    return this.sendCommand(commandParams).pipe(
      map<DynamicApiResponse, RescomApiInvokeAI>((response) => {
        return new RescomApiInvokeAI(response.body);
      }),
      catchError((err: HttpErrorResponse) => {
        throw err?.error?.Errors[0]?.ErrorDesc as string
      })
    );
  }

  invokeAIBulk(params: IRescomApiInvokeAIParams[]): Observable<RescomApiInvokeAI[]> {

    const commandParams = new CoreBaseApiCommandParams({
      url: 'oem/ai/copywriterbulk',
      data: params,
      type: 'POST',
      customBaseUrl: this.pluginUrlAPI
    });

    return this.sendCommand(commandParams).pipe(
      map<DynamicApiResponse, RescomApiInvokeAI[]>((response) => {
        return response.body.map((o: any) => new RescomApiInvokeAI(o));
      }),
      catchError((err: HttpErrorResponse) => {
        throw err?.error?.Errors[0]?.ErrorDesc as string
      })
    );
  }

  getPropertiesLight(params?: IResApiGetPropertiesLight): Observable<ResPropertyLight[]> {
    return this.callDynamicAPI('package/getPropertyLight', params).pipe(
      map<DynamicApiResponse, ResPropertyLight[]>((response) => {
        return response.body?.DT0?.map((o: any) => new ResPropertyLight(o))
      })
    )
  }

  getSinglePropertyLightWithSuiteCategory(params?: IResApiGetPropertiesLight): Observable<ResPropertyLight[]> {
    return this.callDynamicAPI('package/getPropertyLight', params).pipe(
      map<DynamicApiResponse, ResPropertyLight[]>((response) => {
        const property = response.body?.DT0 ?? []
        property[0].SuiteCategories = response.body?.DT1 ?? []
        return property.map((o: any) => new ResPropertyLight(o))
      })
    )
  }

  getPropertiesLightSuiteCategories(propertyID: number): Observable<ResPropertyLightSuiteCategory[]> {
    return this.callDynamicAPI('package/getPropertyLightSuiteCategories', { PropertyID: propertyID }).pipe(
      map<DynamicApiResponse, ResPropertyLightSuiteCategory[]>((response) => {
        return response.body.map((o: any) => new ResPropertyLightSuiteCategory(o))
      })
    )
  }

  getAllCurrenciesRes(): Observable<ResPriceSetupCurrencis[]> {
    return this.getDynamicAPI('priceSetup/getCurrencies').pipe(
      map<DynamicApiResponse, ResPriceSetupCurrencis[]>((response) => {
        return response.body.map((data: any) => new ResPriceSetupCurrencis(data))
      }),
    )
  }

    //Experience/Service rules

    getRules(params: IResApiGetRules): Observable<ResAdmRules[]> {
      return this.callDynamicAPI('experience/getRules', params).pipe(
        map<DynamicApiResponse, ResAdmRules[]>((response) => {
          return response.body.map((data: any) => new ResAdmRules(data));
        })
      )
    }

    getRuleTypes(from: string): Observable<ResRuleType[]> {
      return this.callDynamicAPI('experience/getRuleTypes', {From: from }).pipe(
        map<DynamicApiResponse, ResRuleType[]>((response) => {
          return response.body.map((data: any) => new ResRuleType(data));
        })
      )
    }

    manageRule(params: IResApiManageRule) {
      return this.postDynamicAPI('experience/manageRules', params).pipe(
        map<DynamicApiResponse, any>((response) => {
          return response.body
        })
      )
    }

    //Supplier Unique
    getUniqueSuppliers(params?: IResApiGetUniqueSuppliers): Observable<RescomUniqueSuppliers[]> {
      return this.callDynamicAPI('supplier/getSupplierUnique', params).pipe(
        map<DynamicApiResponse, RescomUniqueSuppliers[]>((response) => {
          return response.body.map((data: any) => new RescomUniqueSuppliers(data));
        })
      )
    }

    manageSupplierUniqueGeneral(params?: IResApiManageSupplierUnique): Observable<any> {
      return this.postDynamicAPI('supplier/manageSupplierUnique', params).pipe(
        map<DynamicApiResponse, any>((response) => {
          return response.body
        })
      )
    }

    getUniqueSupplierDetails(supplierUniqueID: number): Observable<{ addresses: SupplierDetailsAddress[]; contacts: SupplierDetailsContact[] }> {
      return this.callDynamicAPI('supplier/getSupplierUniqueDetails', { SupplierUniqueID: supplierUniqueID }).pipe(
        map<DynamicApiResponse, { addresses: SupplierDetailsAddress[]; contacts: SupplierDetailsContact[] }>((response) => {
          return {
            addresses: (response.body.DT0).map((data: any) => new SupplierDetailsAddress(data)),
            contacts: response.body.DT1.map((data: any) => new SupplierDetailsContact(data)),
          }
        })
      )
    }

    manageSupplierUniqueAddresses(params?: IResApiManageSupplierUniqueAddresses) {
      return this.postDynamicAPI('supplier/manageSupplierUniqueAddress', params).pipe(
        map<DynamicApiResponse, any>((response) => {
          return response.body
        })
      )
    }

    manageSupplierUniqueContacts(params?: IResApiManageSupplierUniqueContacts) {
      return this.postDynamicAPI('supplier/manageSupplierUniqueContact', params).pipe(
        map<DynamicApiResponse, any>((response) => {
          return response.body
        })
      )
    }

    getPackageUpgradeInventory(params?:IResApiGetPackageUpgradeInventory): Observable<RescomUpgradeInventory[]> {
      return this.callDynamicAPI('package/getInventoryUpgrade', params).pipe(
        map<DynamicApiResponse, RescomUpgradeInventory[]>((response) => {
          return response.body.map((data: any) => new RescomUpgradeInventory(data));
        })
      )
    }

    getTopicList(params?: IResApiGetTopic): Observable<ResAdmTopic[]> {
      return this.callDynamicAPI('topic/getTopicList', params).pipe(
        map<DynamicApiResponse, ResAdmTopic[]>((response) => {
          return response.body.map((o: any) => new ResAdmTopic(o))
        })
      )
    }

    getDocumentationListPaginated(params?: IResApiGetDocumentationList, loadOptions?: any): Promise<ResolvedData<ResAdmDocumentationList>> {
      params = this.fillPaginatedParams(params, loadOptions);

      return lastValueFrom(
        this.callDynamicAPI('topic/getDocumentationList', params).pipe(
          map<DynamicApiResponse, ResolvedData<ResAdmDocumentationList>>((response: DynamicApiResponse) => {
            return {
              data: response.body.map((o: any) => new ResAdmDocumentationList(o)),
              totalCount: response.recordsCount,
            };
          })
        )
      ).catch(() => {
        throw 'Data loading errors';
      });
    }

    getTopicSubjectListLookup(params?: IResApiGetTopicSubjectList): Observable<ResAdmTopicSubjectLookup[]> {
      return this.callDynamicAPI('topic/getTopicSubjectListLookup', params).pipe(
        map<DynamicApiResponse, ResAdmTopicSubjectLookup[]>((response) => {
          return response.body.map((o: any) => new ResAdmTopicSubjectLookup(o))
        })
      )
    }

    getTopicGuests(bkgID: number): Observable<ResAdmTopicGuests[]> {
      return this.callDynamicAPI('booking/getTopicGuestDayByDay', { BkgID: bkgID }).pipe(
        map<DynamicApiResponse, ResAdmTopicGuests[]>((response) => {
          return response.body.map((o: any) => new ResAdmTopicGuests(o))
        })
      )
    }

}
